import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../AuthContext';
import SubscriptionManager from './SubscriptionManager';
import SubscriptionModal from './SubscriptionModal';
import PracticeSessionLauncher from './PracticeSessionLauncher';
import PracticeSession from './PracticeSession';
import PointsDisplay from './PointsDisplay';
import DailyCheckinModal from './DailyCheckinModal';
import MentalMathSection from './MentalMathSection';
import UniversalHeader from './UniversalHeader';
import { ChartIcon, CheckCircleIcon, CalendarIcon, StarIcon, TargetIcon, MapIcon, BookIcon, BoltIcon, TrophyIcon, BrainIcon } from './Icons';

/**
 * Enhanced Dashboard Component with Mathematical Playground Design
 * Features: Vibrant colors, geometric patterns, playful interactions
 * Maintains all existing functionality while improving visual design
 * All emojis replaced with professional SVG icons
 */

const EnhancedDashboard = ({
  onStartTest,
  onStartSelectiveTest,
  onNavigateToAdmin,
  onNavigateToQuestions,
  onNavigateToLearningPaths,
  curriculumMaps,
  onStartPracticeSession
}) => {
  const { user, userProfile, testEligibility, signOut } = useAuth();

  const subscription = useMemo(() => ({ is_paid_user: true, has_subscription: true }), []);
  const subscriptionLoading = false;

  const [showQuizResultsModal, setShowQuizResultsModal] = useState(false);
  const [showPracticeSession, setShowPracticeSession] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [userStats, setUserStats] = useState({
    totalTests: 0,
    averageScore: 0,
    testsThisMonth: 0,
    strongestTopic: 'None',
    improvementArea: 'None'
  });
  const [isFixingProfile, setIsFixingProfile] = useState(false);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [questionFormData, setQuestionFormData] = useState({
    yearLevel: 'Year 3',
    topics: {}
  });
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [checkinResult, setCheckinResult] = useState(null);
  const [showMentalMathSection, setShowMentalMathSection] = useState(false);

  useEffect(() => {
    if (curriculumMaps && curriculumMaps[questionFormData.yearLevel]) {
      setQuestionFormData(prev => ({
        ...prev,
        topics: { ...curriculumMaps[questionFormData.yearLevel] }
      }));
    }
  }, [questionFormData.yearLevel, curriculumMaps]);

  const handleLearningPathsClick = () => {
    onNavigateToLearningPaths();
  };

  const handleContinueLearning = (activityData) => {
    handleLearningPathsClick();
  };

  const handlePracticeSessionClick = () => {
    if (onStartPracticeSession) {
      onStartPracticeSession();
    }
  };

  const handleCheckin = (result) => {
    setCheckinResult(result);
    setShowCheckinModal(true);
  };

  const handleMentalMathClick = () => {
    setShowMentalMathSection(true);
  };

  const handleBackFromMentalMath = () => {
    setShowMentalMathSection(false);
  };

  const isPremiumUser = true;
  const isSubscriptionEnabled = true;

  const handleGenerateQuestions = async () => {
    setIsGeneratingQuestions(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/generate-questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          year_level: questionFormData.yearLevel,
          curriculum_map: questionFormData.topics
        })
      });

      const result = await response.json();

      if (result.success) {
        const saveResponse = await fetch(`${backendUrl}/api/questions/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(result.questions.map(q => ({
            question_text: q.question_text,
            topic: q.topic,
            difficulty: q.difficulty,
            year_level: questionFormData.yearLevel,
            options: q.options,
            correct_answer: q.options[q.correct_index],
            correct_index: q.correct_index,
            explanation: q.explanation,
            source: result.generation_method === 'offline' ? 'Offline Pool' : 'AI Generated'
          })))
        });

        const saveResult = await saveResponse.json();
        alert(`Successfully generated ${result.questions.length} questions for ${questionFormData.yearLevel} using ${result.generation_method.toUpperCase()}!\n\n${result.message}\n\nQuestions saved to offline pool: ${saveResult.length > 0 ? saveResult.length + ' questions' : 'Failed to save'}\n\nThese questions will now be available for general math tests!`);
        setShowQuestionForm(false);
      } else {
        alert('Failed to generate questions: ' + result.error);
      }
    } catch (error) {
      console.error('Error generating questions:', error);
      alert('Error generating questions. Please try again.');
    } finally {
      setIsGeneratingQuestions(false);
    }
  };

  const updateTopicDifficulty = (topic, difficulty, value) => {
    setQuestionFormData(prev => ({
      ...prev,
      topics: {
        ...prev.topics,
        [topic]: {
          ...prev.topics[topic],
          [difficulty]: parseInt(value) || 0
        }
      }
    }));
  };

  const handleFixProfile = async () => {
    if (!user?.id || !user?.email) {
      alert('User information not available');
      return;
    }

    setIsFixingProfile(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/auth/fix-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          email: user.email,
          full_name: user.user_metadata?.full_name || user.email.split('@')[0]
        })
      });

      const result = await response.json();

      if (result.success) {
        alert('Profile fixed successfully! Please refresh the page.');
        window.location.reload();
      } else {
        alert('Failed to fix profile: ' + result.message);
      }
    } catch (error) {
      console.error('Error fixing profile:', error);
      alert('Error fixing profile. Please try again.');
    } finally {
      setIsFixingProfile(false);
    }
  };

  useEffect(() => {
    const fetchUserStats = async () => {
      if (user?.id) {
        try {
          const backendUrl = process.env.REACT_APP_BACKEND_URL;
          const response = await fetch(`${backendUrl}/api/test-history/${user.id}`);
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.statistics) {
              setUserStats({
                totalTests: data.statistics.total_tests || 0,
                averageScore: data.statistics.average_score || 0,
                testsThisMonth: data.statistics.tests_this_month || 0,
                strongestTopic: data.statistics.strongest_topic || 'Take your first test!',
                improvementArea: data.statistics.improvement_area || 'Start with any topic'
              });
            }
          }
        } catch (error) {
          console.error('Error fetching user stats:', error);
        }
      }
    };

    fetchUserStats();
  }, [user?.id]);

  const handleSignOut = async () => {
    await signOut();
  };

  // Floating mathematical symbols for decoration
  const mathSymbols = ['∑', '∫', 'π', '∞', '√', '∆', 'θ', '∂'];

  return (
    <div className="mc-body" style={{ background: 'var(--mc-gray-100)', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
      {/* Floating Mathematical Symbols - Subtle Decorative */}
      {mathSymbols.map((symbol, index) => (
        <div
          key={index}
          className="mc-floating-symbol"
          style={{
            top: `${10 + index * 12}%`,
            left: `${5 + index * 11}%`,
          }}
        >
          {symbol}
        </div>
      ))}

      {/* Universal Header */}
      <UniversalHeader
        currentPage="dashboard"
        onNavigateToAdmin={onNavigateToAdmin}
        onNavigateToLearningPaths={onNavigateToLearningPaths}
        onNavigateToQuestions={onNavigateToQuestions}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" style={{ position: 'relative', zIndex: 1 }}>
        {/* Hero Welcome Section */}
        <div className="mc-dashboard-hero mb-12">
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div className="text-center">
              <h1 className="mc-display-1" style={{ color: 'white', marginBottom: '0.5rem' }}>
                Welcome Back, {userProfile?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || 'Student'}!
              </h1>
              <p className="mc-body-large" style={{ color: 'rgba(255, 255, 255, 0.9)', marginBottom: 0 }}>
                Ready to conquer some math challenges today?
              </p>
            </div>

            {/* Points and Check-in - Compact Version */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
              <PointsDisplay onCheckIn={handleCheckin} showCheckin={true} compact={true} />
            </div>
          </div>
        </div>

        {/* Test Eligibility Alert */}
        {testEligibility && (
          <div className={`mc-card mb-8 ${testEligibility.can_take_test ? '' : 'mc-card-tilted'}`} style={{
            border: `3px solid ${testEligibility.can_take_test ? 'var(--mc-lime-green)' : 'var(--mc-sunshine-yellow)'}`,
            background: testEligibility.can_take_test ? 'rgba(160, 255, 31, 0.1)' : 'rgba(255, 214, 10, 0.1)'
          }}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center">
                <div style={{ marginRight: '1rem' }}>
                  {testEligibility.can_take_test ? (
                    <CheckCircleIcon className="w-8 h-8" color="var(--mc-lime-green)" />
                  ) : (
                    <div style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--mc-sunshine-yellow)', borderRadius: '50%', color: 'white', fontSize: '1.25rem', fontWeight: 'bold' }}>!</div>
                  )}
                </div>
                <div>
                  <p className="mc-heading-3" style={{
                    color: testEligibility.can_take_test ? 'var(--mc-charcoal)' : 'var(--mc-charcoal)',
                    marginBottom: '0.25rem'
                  }}>
                    {testEligibility.message}
                  </p>
                  {testEligibility.can_take_test && testEligibility.role === 'student' && (
                    <p className="mc-body-small" style={{ color: 'var(--mc-slate-gray)' }}>
                      Tests completed: {testEligibility.tests_taken} | Remaining: {testEligibility.remaining_tests}
                    </p>
                  )}
                </div>
              </div>

              {!testEligibility.can_take_test && testEligibility.message?.includes('User not found') && (
                <button
                  onClick={handleFixProfile}
                  disabled={isFixingProfile}
                  className="mc-button mc-button-primary"
                >
                  {isFixingProfile ? 'Fixing...' : 'Fix My Account'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* User Progress Stats - Clean Design */}
        <div className="mb-12">
          <h2 className="mc-heading-1" style={{ color: 'var(--mc-charcoal)', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <ChartIcon className="w-8 h-8" color="var(--mc-primary)" />
            Your Progress Overview
          </h2>
          <div className="mc-dashboard-grid">
            {/* Total Tests */}
            <div className="mc-stat-card">
              <div className="mc-stat-icon mc-stat-icon-blue">
                <BookIcon className="w-6 h-6" color="white" />
              </div>
              <div>
                <div className="mc-stat-value" style={{ color: 'var(--mc-electric-blue)' }}>
                  {userStats.totalTests}
                </div>
                <div className="mc-stat-label">Total Tests</div>
              </div>
            </div>

            {/* Average Score */}
            <div className="mc-stat-card">
              <div className="mc-stat-icon mc-stat-icon-green">
                <CheckCircleIcon className="w-6 h-6" color="white" />
              </div>
              <div>
                <div className="mc-stat-value" style={{ color: 'var(--mc-lime-green)' }}>
                  {userStats.averageScore}%
                </div>
                <div className="mc-stat-label">Average Score</div>
              </div>
            </div>

            {/* Tests This Month */}
            <div className="mc-stat-card">
              <div className="mc-stat-icon mc-stat-icon-pink">
                <CalendarIcon className="w-6 h-6" color="white" />
              </div>
              <div>
                <div className="mc-stat-value" style={{ color: 'var(--mc-purple-pop)' }}>
                  {userStats.testsThisMonth}
                </div>
                <div className="mc-stat-label">This Month</div>
              </div>
            </div>

            {/* Strongest Topic */}
            <div className="mc-stat-card">
              <div className="mc-stat-icon mc-stat-icon-orange">
                <StarIcon className="w-6 h-6" color="white" />
              </div>
              <div>
                <div className="mc-heading-3" style={{ color: 'var(--mc-charcoal)', marginBottom: '0.25rem' }}>
                  {userStats.strongestTopic}
                </div>
                <div className="mc-stat-label">Strongest Topic</div>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Insights */}
        {userStats.totalTests > 0 && (
          <div className="mb-12">
            <div className="mc-card mc-card-primary" style={{
              background: 'var(--mc-primary-lighter)'
            }}>
              <h3 className="mc-heading-2" style={{ color: 'var(--mc-primary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <BrainIcon className="w-6 h-6" color="var(--mc-primary)" />
                Your Learning Insights
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="mc-caption" style={{ color: 'var(--mc-gray-600)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <TrophyIcon className="w-5 h-5" color="var(--mc-success)" />
                    You're doing great at:
                  </p>
                  <p className="mc-heading-3" style={{ color: 'var(--mc-charcoal)' }}>
                    {userStats.strongestTopic}
                  </p>
                </div>
                <div>
                  <p className="mc-caption" style={{ color: 'var(--mc-gray-600)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <TargetIcon className="w-5 h-5" color="var(--mc-primary)" />
                    Focus area for improvement:
                  </p>
                  <p className="mc-heading-3" style={{ color: 'var(--mc-charcoal)' }}>
                    {userStats.improvementArea}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Action Cards - Clean Single Color Design */}
        <div className="mb-12">
          <h2 className="mc-heading-1" style={{ color: 'var(--mc-charcoal)', marginBottom: '1.5rem' }}>
            Start Learning
          </h2>
          <div className="mc-dashboard-grid">
            {/* Free Evaluation Test */}
            <div className="mc-card mc-card-interactive">
              <div className="text-center">
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                  <div style={{ width: '72px', height: '72px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--mc-primary-light)', borderRadius: 'var(--radius-lg)' }}>
                    <TargetIcon className="w-10 h-10" color="var(--mc-primary)" />
                  </div>
                </div>
                <h3 className="mc-heading-2" style={{ color: 'var(--mc-charcoal)', marginBottom: '0.75rem' }}>
                  Free Evaluation Test
                </h3>
                <p className="mc-body" style={{ color: 'var(--mc-gray-600)', marginBottom: '1.5rem' }}>
                  Comprehensive assessment covering all key mathematical concepts for your year level.
                </p>
                <button
                  onClick={onStartTest}
                  disabled={!testEligibility?.can_take_test}
                  className={`mc-button mc-button-large w-full ${
                    testEligibility?.can_take_test ? 'mc-button-primary' : ''
                  }`}
                  style={!testEligibility?.can_take_test ? {
                    background: 'var(--mc-gray-400)',
                    cursor: 'not-allowed',
                    opacity: 0.5
                  } : {}}
                >
                  {testEligibility?.can_take_test ? 'Start Test' : 'Limit Reached'}
                </button>
              </div>
            </div>

            {/* Learning Paths */}
            <div className="mc-card mc-card-interactive">
              <div className="text-center">
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                  <div style={{ width: '72px', height: '72px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--mc-primary-light)', borderRadius: 'var(--radius-lg)' }}>
                    <MapIcon className="w-10 h-10" color="var(--mc-primary)" />
                  </div>
                </div>
                <h3 className="mc-heading-2" style={{ color: 'var(--mc-charcoal)', marginBottom: '0.75rem' }}>
                  Personalized Learning
                </h3>
                <p className="mc-body" style={{ color: 'var(--mc-gray-600)', marginBottom: '1.5rem' }}>
                  AI-generated learning journeys tailored to your child's specific strengths and areas for improvement.
                </p>
                <button
                  onClick={handleLearningPathsClick}
                  className="mc-button mc-button-primary mc-button-large w-full"
                >
                  Start Learning
                </button>
              </div>
            </div>

            {/* Practice Sessions */}
            <div className="mc-card mc-card-interactive">
              <div className="text-center">
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                  <div style={{ width: '72px', height: '72px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--mc-primary-light)', borderRadius: 'var(--radius-lg)' }}>
                    <BookIcon className="w-10 h-10" color="var(--mc-primary)" />
                  </div>
                </div>
                <h3 className="mc-heading-2" style={{ color: 'var(--mc-charcoal)', marginBottom: '0.75rem' }}>
                  Practice Sessions
                </h3>
                <p className="mc-body" style={{ color: 'var(--mc-gray-600)', marginBottom: '1.5rem' }}>
                  Focused practice exercises with instant feedback to reinforce learning and build confidence.
                </p>
                <button
                  onClick={handlePracticeSessionClick}
                  className="mc-button mc-button-primary mc-button-large w-full"
                >
                  Start Practice
                </button>
              </div>
            </div>

            {/* Mental Math Games */}
            <div className="mc-card mc-card-interactive" style={{ position: 'relative', overflow: 'visible' }}>
              <div className="mc-badge mc-badge-new" style={{
                position: 'absolute',
                top: '-12px',
                right: '-12px',
                zIndex: 10
              }}>
                NEW!
              </div>
              <div className="text-center">
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                  <div style={{ width: '72px', height: '72px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--mc-primary-light)', borderRadius: 'var(--radius-lg)' }}>
                    <BoltIcon className="w-10 h-10" color="var(--mc-primary)" />
                  </div>
                </div>
                <h3 className="mc-heading-2" style={{ color: 'var(--mc-charcoal)', marginBottom: '0.75rem' }}>
                  Mental Math Games
                </h3>
                <p className="mc-body" style={{ color: 'var(--mc-gray-600)', marginBottom: '1.5rem' }}>
                  Fun, timed games to improve mental calculation speed and number pattern recognition.
                </p>
                <button
                  onClick={handleMentalMathClick}
                  className="mc-button mc-button-primary mc-button-large w-full"
                >
                  Play Games
                </button>
              </div>
            </div>

            {/* Selective Exam Preparation */}
            <div className="mc-card mc-card-interactive" style={{ position: 'relative', overflow: 'visible' }}>
              <div className="mc-badge mc-badge-new" style={{
                position: 'absolute',
                top: '-12px',
                right: '-12px',
                zIndex: 10
              }}>
                FREE!
              </div>
              <div className="text-center">
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                  <div style={{ width: '72px', height: '72px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--mc-primary-light)', borderRadius: 'var(--radius-lg)' }}>
                    <TrophyIcon className="w-10 h-10" color="var(--mc-primary)" />
                  </div>
                </div>
                <h3 className="mc-heading-2" style={{ color: 'var(--mc-charcoal)', marginBottom: '0.75rem' }}>
                  Selective Exam Prep
                </h3>
                <p className="mc-body" style={{ color: 'var(--mc-gray-600)', marginBottom: '1.5rem' }}>
                  NSW Selective &amp; OC test preparation: practice sessions, mock exams, AI writing feedback and mastery tracking.
                </p>
                <button
                  onClick={onStartSelectiveTest}
                  className="mc-button mc-button-primary mc-button-large w-full"
                >
                  Start Preparing
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Admin Section - Preserved functionality */}
        {userProfile?.role === 'admin' && (
          <div className="mt-12">
            <div className="mc-card mc-card-primary" style={{
              background: 'var(--mc-primary-lighter)'
            }}>
              <h3 className="mc-heading-1" style={{ color: 'var(--mc-primary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--mc-primary-light)', borderRadius: 'var(--radius-md)' }}>
                  <ChartIcon className="w-6 h-6" color="var(--mc-primary)" />
                </div>
                Admin Panel
              </h3>
              <p className="mc-body" style={{ color: 'var(--mc-gray-600)', marginBottom: '1.5rem' }}>
                You have administrator privileges with unlimited test access.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={onNavigateToAdmin}
                  className="mc-card mc-card-interactive"
                  style={{ background: 'white', padding: '1rem' }}
                >
                  <div className="mc-heading-3" style={{ color: 'var(--mc-primary)' }}>Platform Dashboard</div>
                  <div className="mc-body-small" style={{ color: 'var(--mc-gray-600)' }}>View usage statistics</div>
                </button>
                <button
                  onClick={onNavigateToQuestions}
                  className="mc-card mc-card-interactive"
                  style={{ background: 'white', padding: '1rem' }}
                >
                  <div className="mc-heading-3" style={{ color: 'var(--mc-primary)' }}>Question Pool</div>
                  <div className="mc-body-small" style={{ color: 'var(--mc-gray-600)' }}>Manage test questions</div>
                </button>
                <button
                  className="mc-card mc-card-interactive"
                  style={{ background: 'white', padding: '1rem', opacity: 0.7, cursor: 'not-allowed' }}
                >
                  <div className="mc-heading-3" style={{ color: 'var(--mc-gray-400)' }}>User Management</div>
                  <div className="mc-body-small" style={{ color: 'var(--mc-gray-600)' }}>Coming soon</div>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modals - Preserved functionality */}
        <DailyCheckinModal
          isOpen={showCheckinModal}
          onClose={() => setShowCheckinModal(false)}
          checkinResult={checkinResult}
        />

        {/* Mental Math Section */}
        {showMentalMathSection && (
          <div className="fixed inset-0 bg-white z-50 overflow-auto">
            <MentalMathSection onBackToDashboard={handleBackFromMentalMath} />
          </div>
        )}
      </main>
    </div>
  );
};

export default React.memo(EnhancedDashboard);
